import React, { useEffect, useState } from "react";

// COMPONENT IMPORTS
import {
  getPaymentAgreementData,
  getPaymentLink,
  paymentLogoGet,
  paymentReceiptAgreementDownloadPdf,
  paymentReceiptDownloadPdf,
  updateLogoAndFavicon,
} from "store/slices/paymentsSlice";
import { useAppDispatch } from "store/hooks";
import Skeleton from "react-loading-skeleton";

// ICONS IMPORTS
import logo from "icons/smallLogo.svg";
import { CloseIcon } from "icons/payments/CloseIcon";
import { EyeIcon } from "icons/payments/EyeIcon";
import { updateUserData, useUserData } from "store/slices/authSlice";
import QuestionsFooter from "components/auth/QuestionsFooter";
import Button from "components/form/Button";
import { OfferIcon } from "icons/payments/OfferIcon";
import InfoIcon from "../../icons/dashboard/InfoIcon";
import {
  changePaymentScheduleName,
  decode,
  downloadPdfFromApi,
  encode,
  formatCurrency,
  getCountDownAgreement,
  isExpired,
  rowHelperText,
} from "utils/HelperFunctions";
import { useNavigate, useParams } from "react-router-dom";
import CustomTooltip from "components/ui-component/CustomTooltip";
import { formatPaymentListDate, formatDisplayDate } from "utils/DateFormat";
import moment from "moment";
import { DownloadIcon } from "icons/payments/DownloadIcon";
import {
  useLeadData,
  usePortalSettingsData,
  usePortalVanityUrl,
} from "store/slices/dashboardSlice";
import PaymentPreview from "components/payments/PaymentPreview";
import { SettingsValue } from "types/payments";
import { FilledInfoIcon } from "icons/payments/FilledInfoIcon";
import { getEffectiveAgreementStatus } from "utils/agreementExpiration";
import { getAgreementBalance } from "utils/agreementBalance";
import { useTestAttrsFactory } from "utils/testid";

// PROPS TYPE
interface ActionPreviewProps {
  id: string;
  setId: any;
  show: boolean;
  setShow: any;
  view: any;
  setView: any;
}

const ActionPreview = (props: ActionPreviewProps) => {
  const { id, setId, show, setShow, view, setView } = props;
  const testAttrs = useTestAttrsFactory();
  const [paymentLogo, setPaymentLogo] = useState<any>(logo);
  const dispatch = useAppDispatch();
  const portalSettings = usePortalSettingsData();
  const vanityUrl = usePortalVanityUrl();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [orderData, setOrderData] = useState<any>({});
  const [scheduled, setScheduled] = useState<any>([]);
  const userData = useUserData();
  const [isOffer, setIsOffer] = useState<boolean>(true);
  const [OfferData, setOfferData] = useState<any>({});
  const [paymentLinkData, setPaymentLinkData] = useState<any>(null);
  const [isDownloading, setIsDownloadLoading] = useState<boolean>(false);
  const [isPaymentDownload, setIsPaymentDownload] = useState<string>("");
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [isPreviewId, setIsPreviewId] = useState<any>("");
  const accountLeadData = useLeadData();

  let accountData = userData?.data?.accountData;
  const navigate = useNavigate();

  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);

  const onGetLogo = async () => {
    try {
      let payload = {
        cluster_id: clusterId,
      };
      await dispatch(paymentLogoGet(payload)).unwrap();
    } catch (error: any) {
      console.log("get logo err: -->", error?.message);
    }
  };

  const onGetPaymentLinkData = async () => {
    try {
      // let slug = encode(id, "pi_");
      let payload = { slug: id };
      let res: any = await dispatch(getPaymentLink(payload)).unwrap();
      const { data, success } = res?.body;
      const { paymentLinkData, companyLogoDetails, customerData } = data;
      if (success) {
        setPaymentLinkData(paymentLinkData);
        setPaymentLogo(companyLogoDetails?.logo);
        dispatch(updateLogoAndFavicon(companyLogoDetails));
        dispatch(
          updateUserData({
            ...userData?.data?.accountData,
            ...customerData,
          })
        );
      }
    } catch (error: any) {
      console.log("get logo err: -->", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onGetPaymentAgreementData = async () => {
    try {
      let payload = {
        slug: id,
      };
      let res: any = await dispatch(getPaymentAgreementData(payload)).unwrap();
      const { data, success } = res?.body;
      if (success) {
        const {
          getAgreementDetailsData,
          companyLogoDetails,
          completion_date,
          cancel_date,
        } = data;
        setPaymentLogo(companyLogoDetails?.logo);
        const agreementExpiryDate =
          getAgreementDetailsData?.expiry_date ||
          getAgreementDetailsData?.expiryDate ||
          getCountDownAgreement(
            getAgreementDetailsData?.pp_oe_time,
            getAgreementDetailsData?.next_billing_date,
            getAgreementDetailsData?.creation_date,
            getAgreementDetailsData?.agreement_tz ||
              userData?.data?.accountData?.timezone
          ).date;
        let newData = {
          ...getAgreementDetailsData,
          agreement_status: getEffectiveAgreementStatus(
            getAgreementDetailsData?.agreement_status,
            agreementExpiryDate
          ),
          completion_date,
          cancel_date,
          grandTotal: getAgreementDetailsData?.amount
            ? getAgreementDetailsData.amount
            : 0,
        };

        setOrderData(newData);
        setIsOffer(
          getAgreementDetailsData?.settlement_offer === SettingsValue.YES
        );
        setOfferData({
          offer_description: getAgreementDetailsData?.offer_description,
          offer_headline: getAgreementDetailsData?.offer_headline,
          offer_amount: getAgreementDetailsData?.offer_amount,
          offer_percentage: getAgreementDetailsData?.offer_percentage,
          offer_type: getAgreementDetailsData?.offer_type,
        });
        let installmentArr: any = [
          ...getAgreementDetailsData?.clients_schedule_custom_data,
        ];

        setScheduled([...installmentArr]);
      }
    } catch (error) {
      console.log("onGetPaymentAgreementData error=====", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!paymentLogo && clusterId && show) onGetLogo();
  }, [clusterId, paymentLogo]);

  useEffect(() => {
    if (!id && !show) return;
    if (view && show) {
      if (view === "payment_link") {
        onGetPaymentLinkData();
      } else if (view === "agreement") {
        onGetPaymentAgreementData();
      }
    }
  }, [id, view, clusterId]);

  useEffect(() => {
    if (!show) {
      onClose();
    }
  }, [show]);

  const onClose = () => {
    setId("");
    setShow(false);
    setScheduled([]);
    setOrderData({});
    setOfferData([]);
    setPaymentLinkData([]);
    setIsPreview(false);
    setIsPreviewId("");
    setIsLoading(true);
    setView("");
  };

  const handleDownloadPDF = async () => {
    let params = {
      plan_id: decode(id)?.split("_")[0],
      lead_id: accountLeadData.lead_id,
      url: `${vanityUrl}${view}/${id}`,
      cluster_id: clusterId,
    };

    let title = view === "agreement" ? "Payment_Agreement" : "Receipt";
    await downloadPdfFromApi(
      () => dispatch(paymentReceiptAgreementDownloadPdf(params)).unwrap(),
      `${title} - ${decode(id)?.split("_")[0]}.pdf`,
      setIsDownloadLoading
    );
  };

  const onPaymentDownload = async (id: any, receiptId?: any) => {
    let pid = encode(`${id}_${clusterId}_${new Date().getTime()}`, "pid_");
    let payload = {
      invoice_id: id,
      lead_id: accountLeadData?.lead_id,
      url: `${vanityUrl}view-payment/${pid}`,
      cluster_id: clusterId,
      isFromPortal: true,
    };

    await downloadPdfFromApi(
      () => dispatch(paymentReceiptDownloadPdf(payload)).unwrap(),
      `Receipt - ${receiptId || id}.pdf`,
      (loading) => setIsPaymentDownload(loading ? id : "")
    );
  };

  return (
    <>
      <div
        className="bg-bgSecondary h-[var(--shell-vh)] overflow-y-auto pt-[70px]"
        {...testAttrs(
          "payment-link-agreement-preview",
          view === "agreement"
            ? "Payment agreement preview"
            : "Payment link preview"
        )}
      >
        <div
          className="lg:px-10 px-5 py-5 bg-bgPrimary flex items-center justify-between fixed top-0 w-full border-b-[1px] border-strokePrimary z-10"
          {...testAttrs("payment-link-agreement-preview-header")}
        >
          <div className="flex items-center gap-2">
            <EyeIcon
              aria-hidden="true"
              strokeColor="var(--color-txt-primary)"
              width="24"
              height="24"
            />
            <div className="lg:text-[26px] text-[22px] text-txtPrimary font-medium leading-[30px]">
              Preview
            </div>
          </div>
          <div className="flex items-center gap-5">
            {view === "agreement" ? (
              <div
                className="flex items-center gap-2 cursor-pointer"
                role="button"
                tabIndex={0}
                {...testAttrs("download-agreement", "Download agreement")}
                onClick={() => {
                  if (view === "agreement") handleDownloadPDF();
                }}
              >
                {isDownloading ? (
                  <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
                ) : (
                  <DownloadIcon
                    aria-hidden="true"
                    strokeColor="var(--color-btn-primary-active)"
                    width="16"
                    height="16"
                  />
                )}
                <span className="text-sm font-medium text-btnPrimaryActive hover:!text-btnPrimaryHover leading-normal underline">
                  {isDownloading ? "Downloading..." : "Download"}
                </span>
              </div>
            ) : view === "payment_link" &&
              paymentLinkData?.is_paid === SettingsValue.YES ? (
              <p
                className="text-sm font-medium text-btnPrimaryActive hover:!text-btnPrimaryHover leading-normal underline cursor-pointer"
                role="button"
                tabIndex={0}
                {...testAttrs(
                  "view-payment-link-receipt",
                  "View payment link receipt"
                )}
                onClick={() => {
                  setIsPreview(true);
                  setIsPreviewId(paymentLinkData?.order_id);
                }}
              >
                View receipt
              </p>
            ) : null}
            <div
              role="button"
              tabIndex={0}
              {...testAttrs("close-preview", "Close preview")}
              onClick={() => onClose()}
              className="cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
            >
              <CloseIcon aria-hidden="true" />
            </div>
          </div>
        </div>

        {isLoading && view === "payment_link" && (
          <div className="lg:px-10 px-5 py-4  lg:text-base text-sm bg-white">
            Payment link <Skeleton width={150} />
          </div>
        )}
        {!isLoading && view === "payment_link" && (
          <div
            className={`lg:px-10 px-5 py-4 lg:text-base text-sm ${
              isExpired(paymentLinkData?.expiry_date) &&
              paymentLinkData?.is_paid !== SettingsValue.YES
                ? "bg-lightRed2"
                : paymentLinkData.is_paid === SettingsValue.YES
                  ? "bg-lightGreen"
                  : "bg-white border-t-[5px] border-strokePrimary"
            }`}
          >
            Payment link{" "}
            {isExpired(paymentLinkData?.expiry_date) &&
            paymentLinkData?.is_paid !== SettingsValue.YES
              ? "expired"
              : paymentLinkData.is_paid === SettingsValue.YES
                ? "completed"
                : "expires"}{" "}
            on{" "}
            <span
              className={`inline-block whitespace-nowrap ${
                isExpired(paymentLinkData?.expiry_date) &&
                paymentLinkData?.is_paid !== SettingsValue.YES
                  ? "text-red"
                  : paymentLinkData.is_paid === SettingsValue.YES
                    ? "text-green"
                    : "text-blue"
              }`}
            >
              {formatPaymentListDate(
                paymentLinkData?.is_paid !== SettingsValue.YES
                  ? paymentLinkData?.expiry_date
                  : paymentLinkData?.orderDetails?.payment_date,
                paymentLinkData?.pl_tz,
                "long"
              )}
            </span>
          </div>
        )}

        {isLoading && view === "agreement" && (
          <div className="lg:px-10 px-5 py-4  lg:text-base text-sm bg-white">
            Payment agreement <Skeleton width={150} />
          </div>
        )}

        {!isLoading && view === "agreement" && (
          <div
            className={`lg:px-10 px-5 py-4  lg:text-base text-sm ${
              orderData.agreement_status === "Expired" ||
              orderData.agreement_status === "Cancelled"
                ? "bg-lightRed2"
                : orderData.agreement_status === "Completed" ||
                    orderData.agreement_status === "Accepted"
                  ? "bg-lightGreen"
                  : "bg-white border-t-[5px] border-strokePrimary"
            }`}
          >
            Payment agreement{" "}
            {orderData.agreement_status === "Expired"
              ? "expired on"
              : orderData.agreement_status === "Cancelled"
                ? "canceled on"
                : orderData.agreement_status === "Completed" ||
                    orderData.agreement_status === "Accepted"
                  ? "completed on"
                  : "expires on"}{" "}
            <span
              className={`${
                orderData.agreement_status === "Expired" ||
                orderData.agreement_status === "Cancelled"
                  ? "text-red"
                  : orderData.agreement_status === "Completed" ||
                      orderData.agreement_status === "Accepted"
                    ? "text-green"
                    : "text-blue"
              }`}
            >
              {orderData.agreement_status === "Cancelled"
                ? moment(
                    orderData.cancel_date || orderData?.next_billing_date
                  ).format("MMM D, YYYY")
                : orderData.agreement_status === "Completed" ||
                    orderData.agreement_status === "Accepted"
                  ? moment(
                      orderData.completion_date || orderData?.next_billing_date
                    ).format("MMM D, YYYY")
                  : orderData?.expiry_date
                    ? moment(orderData.expiry_date).format("MMM D, YYYY")
                    : orderData?.expiryDate
                      ? moment(orderData.expiryDate).format("MMM D, YYYY")
                      : getCountDownAgreement(
                          orderData?.pp_oe_time,
                          orderData.next_billing_date,
                          orderData.creation_date,
                          userData?.data?.accountData?.timezone
                        ).adjustedDate}
            </span>
          </div>
        )}

        <div className="lg:px-10 px-5 py-4 flex items-center justify-between">
          <div className="lg:text-xl text-lg font-normal leading-normal text-txtPrimary">
            {view === "payment_link"
              ? "Payment Link"
              : view === "agreement"
                ? "Payment Agreement"
                : "Invoice"}
          </div>
          <div className="flex items-center gap-[10px]">
            {isLoading ? (
              <Skeleton width={100} height={35} />
            ) : paymentLogo ? (
              <div className="h-16 lg:h-16 flex justify-end">
                <img
                  src={paymentLogo}
                  alt="Company logo"
                  className="animate__animated animate__fadeIn object-contain"
                />
              </div>
            ) : (
              <img
                src={logo}
                alt="Pay portal logo"
                className="animate__animated animate__fadeIn"
              />
            )}
            {!paymentLogo && (
              <div className="text-purple font-medium text-xl leading-[30px]">
                CompanyName
              </div>
            )}
          </div>
        </div>

        <div className="">
          <div className="flex justify-between pb-2 lg:px-10 px-5 items-center">
            <p className="text-btnPrimaryActive text-xl font-normal">
              {isLoading ? (
                <Skeleton width={150} />
              ) : (
                `${
                  accountData?.first_name + " " + accountData?.last_name || ""
                }`
              )}
            </p>
            <p className="flex text-sm text-txtPrimary">
              {" "}
              {isLoading ? <Skeleton width={70} /> : `${accountData.accountid}`}
            </p>
          </div>
          <div className=" lg:px-10 px-5 flex justify-between gap-1 mb-3">
            <div className="w-full flex text-sm gap-1 break-words text-txtSecondary">
              Balance
              <p className="text-txtPrimary">
                {isLoading ? (
                  <Skeleton width={70} />
                ) : (
                  formatCurrency(
                    view === "agreement"
                      ? getAgreementBalance(
                          orderData?.initial_balance,
                          accountData?.balance
                        )
                      : accountData?.balance
                  ) || "$0.00"
                )}
              </p>
            </div>
            <div className="w-full flex text-sm gap-1 justify-end text-txtSecondary">
              <p className="w-full break-words text-end">
                Description{" "}
                <span className="text-txtPrimary">
                  {isLoading ? (
                    <Skeleton width={70} />
                  ) : (
                    `${accountData?.description ?? "--"}`
                  )}
                </span>
              </p>
            </div>
          </div>

          {view === "payment_link" && (
            <>
              <div className="h-[1px] w-full bg-strokePrimary lg:my-5 my-4" />

              <div className="lg:px-10 px-5 flex justify-between text-txtSecondary text-sm mb-1">
                <p className="">AMOUNT</p>
                <p className="">DUE DATE</p>
              </div>
              <div className="lg:px-10 px-5 flex justify-between items-center">
                <p className="text-2xl text-txtPrimary">
                  {isLoading ? (
                    <Skeleton width={70} height={24} />
                  ) : paymentLinkData?.is_editable === "Y" &&
                    !paymentLinkData?.amount ? (
                    "--"
                  ) : (
                    formatCurrency(paymentLinkData?.amount) || "$0.00"
                  )}
                </p>
                <p className="lg:text-base text-sm text-txtPrimary mt-1">
                  {isLoading ? (
                    <Skeleton width={70} height={14} />
                  ) : (
                    `${formatDisplayDate(paymentLinkData?.due_date, "MM/DD/YYYY")}`
                  )}
                </p>
              </div>
              {!isLoading &&
              !isExpired(paymentLinkData?.expiry_date) &&
              paymentLinkData.is_paid === "N" ? (
                <>
                  <div className="w-full mt-7 lg:px-10 px-5">
                    <Button
                      text="Pay"
                      className="!w-full"
                      variant="variable"
                      {...testAttrs("pay-payment-link", "Pay payment link")}
                      onClick={() => {
                        const now = new Date();
                        let encodedString = encode(
                          `${
                            paymentLinkData?.id
                          }_${clusterId}_${now.getTime()}`,
                          "pl_"
                        );
                        navigate(`/payment_link/${id || encodedString}`);
                        setShow(false);
                        setId("");
                      }}
                    />
                  </div>
                </>
              ) : (
                ""
              )}

              {!isLoading && (
                <div className={`py-5 lg:px-10 px-5 bottom-0 fixed`}>
                  <QuestionsFooter isPreview={true} />
                </div>
              )}
            </>
          )}

          {view === "agreement" && (
            <>
              <div className="lg:px-10 px-5 pb-2">
                <div className=" py-5 bg-bgPrimary border-x border-t border-strokePrimary rounded-t-lg mt-2">
                  <div className="flex flex-col items-start gap-1">
                    <div className="px-6 text-sm leading-normal font-bold uppercase">
                      {isOffer ? "OFFER TERMS" : "TERMS"}
                    </div>
                    {isOffer ? (
                      ""
                    ) : (
                      <div className="px-6 w-full">
                        <div className=" mt-2 h-[1px] w-full bg-strokePrimary" />
                      </div>
                    )}

                    <div className="flex flex-col items-start w-full">
                      {isOffer ? (
                        <>
                          <div className="flex bg-bgThird w-full gap-4 px-6 py-5 mt-[12px]">
                            <div>
                              <OfferIcon />
                            </div>
                            <div>
                              <p className="text-xl font-bold text-txtPrimary">
                                {OfferData?.offer_headline}
                              </p>
                              <p className="text-sm font-normal text-txtParagraph">
                                {OfferData?.offer_description}
                              </p>
                            </div>
                          </div>
                        </>
                      ) : (
                        ""
                      )}
                    </div>
                    <div className="px-6 pt-4 flex flex-col items-start gap-5 w-full ">
                      <div className=" flex items-center justify-between w-full">
                        <div className="font-normal flex items-center gap-1 cursor-pointer text-sm leading-normal text-txtParagraph">
                          <p>Start date </p>
                          <CustomTooltip
                            maxwidth="430px"
                            place="top"
                            id="start-date-anchor"
                            content={`<div class="text-xs leading-[18px] font-normal">The date when the first payment installment is scheduled to be processed.</div>`}
                          >
                            <InfoIcon
                              id="start-date-anchor"
                              className="cursor-pointer group"
                              _className="group-hover:!stroke-iconObscure"
                            />
                          </CustomTooltip>
                        </div>
                        <div className="inline-block whitespace-nowrap font-medium text-sm leading-normal text-txtPrimary text-right">
                          {isLoading ? (
                            <Skeleton width={150} />
                          ) : (
                            formatPaymentListDate(
                              orderData?.billing_start_date,
                              orderData?.agreement_tz,
                              "long"
                            )
                          )}
                        </div>
                      </div>
                      <div className="h-[1px] w-full bg-strokePrimary" />
                      <div className="flex items-center justify-between w-full">
                        <div className="font-normal flex items-center gap-1 cursor-pointer text-sm leading-normal text-txtParagraph">
                          <p>Frequency</p>{" "}
                          <div className="grid place-items-center">
                            <CustomTooltip
                              maxwidth="430px"
                              place="top"
                              id="frequency-anchor"
                              content={`<div class="text-xs leading-[18px] font-normal">Defines how often the installments will be processed.</div>`}
                            >
                              <div
                                id="frequency-anchor"
                                className="cursor-pointer pointer-events-auto"
                              >
                                <InfoIcon />
                              </div>
                            </CustomTooltip>
                          </div>
                        </div>
                        <div className="font-normal text-sm leading-normal text-txtPrimary">
                          {isLoading ? (
                            <Skeleton width={80} />
                          ) : (
                            <>
                              {changePaymentScheduleName(
                                orderData?.payment_schedule
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="h-[1px] w-full bg-strokePrimary" />

                      <div className="flex items-center justify-between w-full">
                        <div className="flex font-normal items-center gap-1 cursor-pointer text-sm leading-normal text-txtParagraph">
                          <p>Number of transactions</p>
                          <div className="grid place-items-center">
                            <CustomTooltip
                              maxwidth="280px"
                              place="top"
                              id="transaction-anchor"
                              content={`<div class="text-xs leading-[18px] font-normal">The total number of payment installments within this schedule.</div>`}
                            >
                              <div
                                id="transaction-anchor"
                                className="cursor-pointer pointer-events-auto"
                              >
                                <InfoIcon />
                              </div>
                            </CustomTooltip>
                          </div>
                        </div>
                        <div className="font-normal text-sm leading-normal text-txtPrimary">
                          {isLoading ? (
                            <Skeleton width={80} />
                          ) : (
                            orderData?.total_payments
                          )}
                        </div>
                      </div>

                      {isOffer && (
                        <>
                          <div className="h-[1px] w-full bg-strokePrimary" />
                          <div className="flex items-center justify-between w-full">
                            <div className="flex font-bold items-center gap-1 text-sm leading-normal text-txtPrimary">
                              <p>Discount amount</p>
                              <div className="grid place-items-center">
                                <CustomTooltip
                                  maxwidth="430px"
                                  place="top"
                                  id="total-payments-anchor"
                                  content={`<div class="text-xs leading-[18px] font-normal">Total discounted amount for this plan.</div>`}
                                >
                                  <div
                                    id="total-payments-anchor"
                                    className="cursor-pointer pointer-events-auto"
                                  >
                                    <InfoIcon />
                                  </div>
                                </CustomTooltip>
                              </div>
                            </div>
                            <div className="font-bold text-sm text-txtPrimary">
                              {isLoading ? (
                                <Skeleton width={80} />
                              ) : (
                                formatCurrency(
                                  OfferData?.offer_type === "P" &&
                                    (OfferData?.offer_percentage || 0) > 0
                                    ? (OfferData?.offer_percentage *
                                        orderData?.initial_balance) /
                                        100
                                    : OfferData?.offer_amount
                                ) || "$0.00"
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="px-6 py-5 bg-bgThird border-x border-b border-strokePrimary rounded-b-lg border-t ">
                  <div className="flex items-center justify-between w-full">
                    <div className="text-xl leading-normal font-bold flex justify-center items-center text-txtPrimary gap-1">
                      <span className="uppercase">Total</span>{" "}
                      <CustomTooltip
                        maxwidth="430px"
                        place="top"
                        id="total-payments-anchor"
                        content={`<div class="text-xs leading-[18px] font-normal">Total of all scheduled payments across the payment plan’s duration.</div>`}
                      >
                        <div
                          id="total-payments-anchor"
                          className="cursor-pointer pointer-events-auto"
                        >
                          <InfoIcon />
                        </div>
                      </CustomTooltip>
                    </div>

                    <div className="text-xl flex items-center gap-2 text-txtPrimary font-bold">
                      {isLoading ? (
                        <Skeleton width={100} />
                      ) : isOffer ? (
                        formatCurrency(orderData?.totalAmount) || "$0.00"
                      ) : (
                        formatCurrency(orderData?.totalAmount) || "$0.00"
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* </div> */}
              {isLoading && (
                <div className="lg:px-10 px-5 pt-2 animate__animated animate__fadeIn">
                  <div className="flex flex-col items-start gap-4 w-full">
                    <div className="lg:px-6 px-4 py-5 bg-bgPrimary border border-strokePrimary rounded-lg w-full overflow-x-scroll">
                      <div className="flex flex-col items-start gap-5">
                        <div className="flex items-center justify-between w-full">
                          <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
                            SCHEDULE
                          </div>
                        </div>
                        <div className="h-[1px] w-full bg-strokePrimary min-w-[230px]" />
                        <div className="flex items-center w-full justify-between">
                          <div
                            className={`${
                              orderData?.c_FeeAmount > 0 && !isOffer
                                ? "w-1/4"
                                : "w-1/3"
                            } min-w-[35px] text-sm text-txtParagraph leading-normal text-start`}
                          >
                            #
                          </div>
                          <div
                            className={`${
                              orderData?.c_FeeAmount > 0 && !isOffer
                                ? "w-1/4"
                                : "w-1/3"
                            } min-w-[96px] text-sm text-txtParagraph leading-normal text-start`}
                          >
                            Payment Date
                          </div>

                          {orderData?.c_FeeAmount && !isOffer ? (
                            <div
                              className={`${
                                orderData?.c_FeeAmount && !isOffer
                                  ? "w-[20%]"
                                  : "w-1/3"
                              } min-w-[56px] text-sm text-txtParagraph leading-normal text-center`}
                            >
                              Fee
                            </div>
                          ) : null}
                          <div
                            className={`${
                              orderData?.c_FeeAmount && !isOffer
                                ? "w-[20%]"
                                : "w-1/3"
                            } min-w-[96px] text-sm text-txtParagraph leading-normal text-end`}
                          >
                            Amount
                          </div>
                        </div>
                        <div className="h-[1px] w-full bg-strokePrimary min-w-[230px]" />
                        {[1, 2, 3, 4, 5].map((_, index) => (
                          <div
                            className="flex items-center w-full justify-between"
                            key={index}
                          >
                            <div
                              className={`${
                                orderData?.c_FeeAmount && !isOffer
                                  ? "w-1/4"
                                  : "w-1/3"
                              } min-w-[35px] text-sm text-txtPrimary leading-normal text-start flex gap-[6px] items-center`}
                            >
                              {`${index + 1}` + rowHelperText(index + 1)}
                            </div>
                            <div
                              className={`${
                                orderData?.c_FeeAmount && !isOffer
                                  ? "w-1/4"
                                  : "w-1/3"
                              }  min-w-[96px] text-sm text-txtPrimary leading-normal text-start flex gap-[6px] items-center`}
                            >
                              <Skeleton width={95} />
                            </div>
                            <div
                              className={`${
                                orderData?.c_FeeAmount && !isOffer
                                  ? "w-1/4"
                                  : "w-1/3"
                              }  min-w-[96px] text-sm text-txtPrimary leading-normal text-start flex gap-[6px] items-center justify-end`}
                            >
                              <Skeleton width={100} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {scheduled?.length && !isLoading ? (
                <div className="lg:px-10 px-5 py-2 animate__animated animate__fadeIn">
                  <div className="flex flex-col items-start gap-2 w-full">
                    <div className="lg:px-6 px-4 py-5 bg-bgPrimary border border-strokePrimary rounded-lg w-full overflow-x-scroll">
                      <div className="flex flex-col items-start gap-5">
                        <div className="flex items-center justify-between w-full">
                          <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
                            SCHEDULE
                          </div>
                        </div>
                        <div className="h-[1px] w-full bg-strokePrimary min-w-[230px]" />
                        <div className="flex items-center w-full justify-between">
                          <div
                            className={`${
                              orderData?.c_FeeAmount > 0 && !isOffer
                                ? "w-1/4"
                                : "w-1/3"
                            } min-w-[35px] text-sm text-txtParagraph leading-normal text-start`}
                          >
                            #
                          </div>
                          <div
                            className={`${
                              orderData?.c_FeeAmount > 0 && !isOffer
                                ? "w-1/4"
                                : "w-1/3"
                            } min-w-[96px] text-sm text-txtParagraph leading-normal text-start`}
                          >
                            Payment Date
                          </div>

                          {orderData?.c_FeeAmount && !isOffer ? (
                            <div
                              className={`${
                                orderData?.c_FeeAmount && !isOffer
                                  ? "w-[20%]"
                                  : "w-1/3"
                              } min-w-[56px] text-sm text-txtParagraph leading-normal text-center`}
                            >
                              Fee
                            </div>
                          ) : null}
                          <div
                            className={`${
                              orderData?.c_FeeAmount && !isOffer
                                ? "w-[20%]"
                                : "w-1/3"
                            } min-w-[96px] text-sm text-txtParagraph leading-normal text-end`}
                          >
                            Amount
                          </div>
                        </div>
                        <div className="h-[1px] w-full bg-strokePrimary min-w-[230px]" />
                        {scheduled?.length
                          ? scheduled?.map((x: any, idx: number) => {
                              return (
                                <div
                                  className="flex items-center w-full justify-between"
                                  key={idx}
                                >
                                  <div
                                    className={`${
                                      orderData?.c_FeeAmount && !isOffer
                                        ? "w-1/4"
                                        : "w-1/3"
                                    } min-w-[35px] text-sm text-txtPrimary leading-normal text-start flex gap-[6px] items-center`}
                                  >
                                    {`${idx + 1}` + rowHelperText(idx + 1)}
                                  </div>
                                  <div
                                    className={`${
                                      orderData?.c_FeeAmount && !isOffer
                                        ? "w-1/4"
                                        : "w-1/3"
                                    }  min-w-[96px] text-sm text-txtPrimary leading-normal text-start flex gap-[6px] items-center`}
                                  >
                                    {x?.payment_date
                                      ? moment(x?.payment_date).format(
                                          "MM/DD/YYYY"
                                        )
                                      : x?.billing_date
                                        ? moment(x?.billing_date).format(
                                            "MM/DD/YYYY"
                                          )
                                        : "--"}
                                  </div>
                                  {orderData?.c_FeeAmount && !isOffer ? (
                                    <div
                                      className={`${
                                        orderData?.c_FeeAmount && !isOffer
                                          ? "w-[20%]"
                                          : "w-1/3"
                                      } min-w-[56px] text-sm text-txtPrimary leading-normal text-center`}
                                    >
                                      {x?.cf_amount
                                        ? formatCurrency(x?.cf_amount)
                                        : "$0.00"}
                                    </div>
                                  ) : null}
                                  <div
                                    className={`${
                                      orderData?.c_FeeAmount && !isOffer
                                        ? "w-[20%]"
                                        : "w-1/3"
                                    } min-w-[96px] text-sm text-txtPrimary leading-normal text-end`}
                                  >
                                    {x?.amount
                                      ? formatCurrency(x?.amount)
                                      : "$0.00"}
                                  </div>
                                </div>
                              );
                            })
                          : null}
                        <div className="h-[1px] w-full bg-strokePrimary min-w-[230px]" />
                        <div className="flex items-center w-full justify-between min-w-[230px]">
                          <div
                            className={`${
                              orderData?.c_FeeAmount && !isOffer
                                ? "w-[35%]"
                                : "w-1/3"
                            } text-xs font-medium text-txtPrimary`}
                          >
                            SUBTOTAL
                          </div>
                          <div
                            className={`${
                              orderData?.c_FeeAmount && !isOffer
                                ? "w-1/4"
                                : "w-1/3"
                            }`}
                          ></div>
                          {orderData?.c_FeeAmount && !isOffer ? (
                            <div
                              className={`${
                                orderData?.c_FeeAmount && !isOffer
                                  ? "w-"
                                  : "w-1/3"
                              } text-center text-txtPrimary font-bold leading-normal`}
                            >
                              {formatCurrency(orderData?.c_FeeAmount_total) ||
                                "0.00"}
                            </div>
                          ) : null}
                          <div
                            className={`${
                              orderData?.c_FeeAmount && !isOffer
                                ? "w-1/4"
                                : "w-1/3"
                            } text-end text-txtPrimary font-bold leading-normal`}
                          >
                            {formatCurrency(orderData?.totalAmount) || "0.00"}
                          </div>
                        </div>
                        <div className="h-[1px] w-full bg-strokePrimary min-w-[230px]" />
                        <div className="flex items-start justify-between w-full min-w-[230px]">
                          <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
                            Total
                          </div>
                          <div className="text-lg leading-normal font-bold text-txtPrimary">
                            {isLoading ? (
                              <Skeleton width={100} />
                            ) : isOffer ? (
                              formatCurrency(orderData?.totalAmount) || "0.00"
                            ) : (
                              formatCurrency(orderData?.totalAmount) || "0.00"
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    {orderData?.agreement_status === "Pending" && (
                      <div className="flex items-center gap-2 text-xs font-normal leading-normal text-txtParagraph ">
                        <FilledInfoIcon />
                        <p>
                          A{" "}
                          <span className="lowercase">
                            {portalSettings?.cc_fee_label
                              ? portalSettings?.cc_fee_label
                              : "convenience fee"}
                          </span>{" "}
                          may apply to each payment.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {orderData?.agreement_status === "Opened" ? (
                <>
                  <div className="w-full mt-7 px-10">
                    <Button
                      text="Pay"
                      className="!w-full"
                      variant="variable"
                      {...testAttrs("pay-agreement", "Pay payment agreement")}
                      onClick={() => {
                        const now = new Date();
                        let encodedString = encode(
                          `${id}_${clusterId}_${now.getTime()}`,
                          "agr_"
                        );
                        navigate(`/agreement/${id}`);
                      }}
                    />
                  </div>
                </>
              ) : (
                ""
              )}
              {!isLoading && (
                <div
                  className={`py-5 lg:px-10 px-5 bottom-0 ${
                    scheduled?.length && !isLoading ? "static" : "fixed"
                  }`}
                >
                  <QuestionsFooter isPreview={true} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <div
        className="absolute top-0 lg:w-[590px] h-full lg:right-[590px] right-0 z-10 w-full"
        style={{ display: isPreview ? "block" : "none" }}
        {...testAttrs("payment-link-receipt-preview")}
      >
        <PaymentPreview
          id={isPreviewId}
          setId={setIsPreviewId}
          show={isPreview}
          setShow={setIsPreview}
          onDownloadCLick={(id: any, receiptId: any) =>
            onPaymentDownload(id, receiptId)
          }
          isDownloading={isPaymentDownload?.toString()}
        />
      </div>
    </>
  );
};

export default ActionPreview;
